import React from 'react';
import PropTypes from 'prop-types';
import { Chip, IconButton, RaisedButton } from 'material-ui';
import _ from 'lodash';

import StevedoringData from './StevedoringData';
import { CheckDiffText } from '../../../common/utils';
import { isProcessedHistoryEntry } from '../../../common/historyUtils';
import InfoIcon from 'material-ui/svg-icons/action/info';
import { getRecommendedTitle } from 'helpers/ports';

const styles = {
    btnBack: {
        labelStyle: { color: '#fff', textTransform: 'none', fontSize: '13px' },
        style: { margin: '20px 0 70px 0' },
    },
    btnSubmit: {
        labelStyle: { color: '#fff', textTransform: 'none', fontSize: '13px' },
        style: { margin: '20px 0 70px 0' },
    },
};

const ShowDiffEmails = ({ emails = [], providerEmails = [] }) => (
    <React.Fragment>
        {emails.map((email, i) => (
            <div key={i} className='phone'>
                <CheckDiffText
                    newValue={providerEmails[i]}
                    oldValue={email}
                />
            </div>
        ))}
        {providerEmails.slice(emails.length).map((email, i) => (
            <div key={`new-${i}`} className='email'>
                <CheckDiffText
                    newValue={email}
                    oldValue={null}
                />
            </div>
        ))}
    </React.Fragment>
);

const ShowDiffPhones = ({ phones = [], providerPhones = [] }) => (
    <React.Fragment>
        {phones.map((phone, i) => (
            <div key={i} className='phone'>
                <CheckDiffText
                    newValue={providerPhones[i] ? providerPhones[i].code : null}
                    oldValue={phone.code}
                />
                <CheckDiffText
                    newValue={providerPhones[i] ? providerPhones[i].phone : null}
                    oldValue={phone.phone}
                />
            </div>
        ))}
        {providerPhones.slice(phones.length).map((phone, i) => (
            <div key={`new-${i}`} className='phone'>
                <CheckDiffText
                    newValue={phone.code}
                    oldValue={null}
                />
                <CheckDiffText
                    newValue={phone.phone}
                    oldValue={null}
                />
            </div>
        ))}
    </React.Fragment>
);

const ActionButtons = ({ onApprove, onReject }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div>
            <RaisedButton
                label='Approve'
                labelStyle={styles.btnSubmit.labelStyle}
                labelPosition='before'
                backgroundColor='#a4c639'
                onClick={onApprove}
            />
        </div>
        <div>
            <RaisedButton
                label='Reject'
                labelStyle={styles.btnSubmit.labelStyle}
                labelPosition='before'
                backgroundColor='#f00'
                onClick={onReject}
            />
        </div>
    </div>
);

const ServiceProvider = ({
    serviceProvider,
    rejectServiceProvider,
    approveServiceProvider,
}) => {
    const pendingHistoryValues = React.useMemo(() => {
        if (!serviceProvider.history) {
            return null;
        }

        return {
            original: serviceProvider.history.originalValues || {},
            changed: serviceProvider.history.changedValues || serviceProvider.provider || {},
        };
    }, [serviceProvider]);

    const isChanged = React.useMemo(() => {
        if (serviceProvider.status === 'remove') return true;
        if (serviceProvider.status !== 'change') return false;

        const changes = [
            [serviceProvider.provider.activities.join(','), serviceProvider.activities.join(',')],
            [serviceProvider.provider.name, serviceProvider.name],
            [serviceProvider.emails, serviceProvider.provider.emails],
            [serviceProvider.phones, serviceProvider.provider.phones],
            [serviceProvider.provider.address, serviceProvider.address],
            [serviceProvider.provider.PIC, serviceProvider.PIC],
            [serviceProvider.provider.limitations, serviceProvider.limitations],
            [(serviceProvider.provider.companyId && serviceProvider.provider.companyId._id) || '', (serviceProvider.companyId && serviceProvider.companyId._id) || ''],
            [(serviceProvider.provider.contactPersonId && serviceProvider.provider.contactPersonId._id) || '', (serviceProvider.contactPersonId && serviceProvider.contactPersonId._id) || ''],
            [(serviceProvider.provider.recommended && serviceProvider.provider.recommended.endAt) || '', (serviceProvider.recommended && serviceProvider.recommended.endAt) || ''],
        ];

        return changes.some(([newValue, oldValue]) => !_.isEqual(newValue, oldValue));
    }, [serviceProvider]);

    const isStevedoring = activities =>
        Array.isArray(activities) && activities.includes('Stevedoring Company');

    const renderChangeView = () => {
        const original = pendingHistoryValues ? pendingHistoryValues.original : serviceProvider;
        const changed = pendingHistoryValues ? pendingHistoryValues.changed : serviceProvider.provider;

        return (
            <div className='table-row'>
                <div className='table-col info'>
                    <div className='name'>
                        {isStevedoring(original.activities) ||
                            isStevedoring(changed.activities) ?
                            (<StevedoringData serviceProvider={serviceProvider} />) :
                            (
                                <CheckDiffText
                                    newValue={(changed.activities || []).join(',')}
                                    oldValue={(original.activities || []).join(',')}
                                />
                            )
                        }
                    </div>
                </div>
                <div className='table-col'>
                    <div className='name company'>
                        <CheckDiffText
                            newValue={changed.name}
                            oldValue={original.name}
                        />
                    </div>
                </div>
                <div className='table-col'>
                    <div className='list'>
                        <ShowDiffEmails
                            emails={original.emails}
                            providerEmails={changed.emails}
                        />
                    </div>
                    <div className='list'>
                        <ShowDiffPhones
                            phones={original.phones}
                            providerPhones={changed.phones}
                        />
                    </div>
                    <div className='name'>
                        <CheckDiffText
                            newValue={changed.address}
                            oldValue={original.address}
                        />
                    </div>
                    <div className='name'>
                        PIC:
                        <CheckDiffText
                            newValue={changed.PIC}
                            oldValue={original.PIC}
                        />
                    </div>
                </div>
                <div
                    className="table-col edit"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        width: '122px',
                    }}
                >
                    {serviceProvider.recommended && serviceProvider.recommended.endAt ? (
                        <Chip
                            style={{
                                backgroundColor: '#E8F5E9',
                                height: 24,
                                position: 'absolute',
                                top: 10,
                                right: 0,
                                zIndex: 1000,
                                width: 118,
                            }}
                            labelStyle={{
                                color: '#2E7D32',
                                lineHeight: '24px',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            Recommended
                            <IconButton
                                tooltip={getRecommendedTitle(serviceProvider)} // no import for now
                                tooltipPosition='bottom-left'
                                style={{
                                    width: 20,
                                    height: 18,
                                    padding: 0,
                                }}
                                iconStyle={{ width: 16, height: 16, color: '#2E7D32' }}
                            >
                                <InfoIcon />
                            </IconButton>
                        </Chip>
                    ) : null}
                    {isChanged && (
                        <ActionButtons
                            onApprove={() => approveServiceProvider(serviceProvider)}
                            onReject={() => rejectServiceProvider(serviceProvider)}
                        />
                    )}
                </div>
            </div>
        );
    };

    const renderRemoveView = () => (
        <div className='table-row dirty-element'>
            <div className='table-col info'>
                <div className='name'>
                    {isStevedoring(serviceProvider.activities) ? (
                        <StevedoringData serviceProvider={serviceProvider} />
                    ) : (
                        <CheckDiffText
                            newValue={null}
                            oldValue={serviceProvider.activities}
                        />
                    )}
                </div>
            </div>
            <div className='table-col'>
                <div className='name company'>
                    <CheckDiffText
                        newValue={null}
                        oldValue={serviceProvider.name}
                    />
                </div>
            </div>
            <div className='table-col'>
                <div className='list'>
                    {serviceProvider.emails.map((email, emailIndex) => (
                        <div key={emailIndex} className='email'>
                            <CheckDiffText
                                newValue={null}
                                oldValue={email}
                            />
                        </div>
                    ))}
                </div>
                <div className='list'>
                    {serviceProvider.phones.map((phone, phoneIndex) => (
                        <div key={phoneIndex} className='phone'>
                            <CheckDiffText
                                newValue={null}
                                oldValue={phone.code}
                            />
                            <CheckDiffText
                                newValue={null}
                                oldValue={phone.phone}
                            />
                        </div>
                    ))}
                </div>
                <div className='name'>
                    <CheckDiffText
                        newValue={null}
                        oldValue={serviceProvider.address}
                    />
                </div>
                <div className='name'>
                    PIC:
                    <CheckDiffText
                        newValue={null}
                        oldValue={serviceProvider.PIC}
                    />
                </div>
            </div>
            <div className='table-col edit'>
                {isChanged && (
                    <ActionButtons
                        onApprove={() => approveServiceProvider(serviceProvider, { isRemove: true })}
                        onReject={() => rejectServiceProvider(serviceProvider, { isRemove: true })}
                    />
                )}
            </div>
        </div>
    );

    const renderNormalView = () => (
        <div className={serviceProvider.status === 'new' ? 'dirty-element table-row' : 'table-row'}>
            <div className='table-col info'>
                <div className='name'>
                    {isStevedoring(serviceProvider.activities) ? (
                        <StevedoringData serviceProvider={serviceProvider} />
                    ) : (
                        serviceProvider.activities.join(',')
                    )}
                </div>
            </div>
            <div className='table-col'>
                <div className='name company'>
                    {serviceProvider.name}
                </div>
            </div>
            <div className='table-col'>
                <div className='list'>
                    {serviceProvider.emails.map((email, emailIndex) => (
                        <div key={emailIndex} className='email'>
                            {email}
                        </div>
                    ))}
                </div>
                <div className='list'>
                    {serviceProvider.phones.map((phone, phoneIndex) => (
                        <div key={phoneIndex} className='phone'>
                            {`${phone.code} ${phone.phone}`}
                        </div>
                    ))}
                </div>
                <div className='name'>
                    {serviceProvider.address}
                </div>
                <div className='name'>
                    PIC: {serviceProvider.PIC}
                </div>
            </div>
            <div className='table-col edit'>
                {serviceProvider.status === 'new' && (
                    <ActionButtons
                        onApprove={() => approveServiceProvider(serviceProvider, { isNew: true })}
                        onReject={() => rejectServiceProvider(serviceProvider, { isNew: true })}
                    />
                )}
            </div>
        </div>
    );

    switch (serviceProvider.status) {
        case 'change':
            return renderChangeView();
        case 'remove':
            return renderRemoveView();
        case 'normal':
        case 'new':
            return renderNormalView();
        default:
            return null;
    }
};

ServiceProvider.propTypes = {
    serviceProvider: PropTypes.shape({
        status: PropTypes.oneOf(['change', 'remove', 'normal', 'new']).isRequired,
        activities: PropTypes.arrayOf(PropTypes.string).isRequired,
        name: PropTypes.string.isRequired,
        emails: PropTypes.arrayOf(PropTypes.string).isRequired,
        phones: PropTypes.arrayOf(PropTypes.shape({
            code: PropTypes.string.isRequired,
            phone: PropTypes.string.isRequired,
        })).isRequired,
        address: PropTypes.string.isRequired,
        PIC: PropTypes.string.isRequired,
        provider: PropTypes.shape({
            activities: PropTypes.arrayOf(PropTypes.string).isRequired,
            name: PropTypes.string.isRequired,
            emails: PropTypes.arrayOf(PropTypes.string).isRequired,
            phones: PropTypes.arrayOf(PropTypes.shape({
                code: PropTypes.string.isRequired,
                phone: PropTypes.string.isRequired,
            })).isRequired,
            address: PropTypes.string.isRequired,
            PIC: PropTypes.string.isRequired,
        }),
    }).isRequired,
    rejectServiceProvider: PropTypes.func.isRequired,
    approveServiceProvider: PropTypes.func.isRequired,
};

export default ServiceProvider;



// WEBPACK FOOTER //
// ./app/components/Regions/PortManagement/blocks/ServiceProviders/ServiceProvidersApproval/ServiceProvider.jsx